/**
 * Legal Pages Manager
 * /admin/legal-pages
 *
 * Manage CMS content for the 3 legal pages:
 *   • Privacy Policy       (/privacy_policy)
 *   • Data Disclaimer      (/disclaimer)
 *   • Terms of Service     (/terms)
 *
 * Each page has: title, subtitle, last_updated, intro, footer_note,
 * SEO metadata, and a list of sections (title + HTML body) which
 * admins can add / edit / delete / reorder.
 *
 * Data is stored in the same `page_content` collection used by all other
 * CMS-driven pages via `PUT /api/cms/pages/:slug`.
 */
import { useState, useEffect } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import {
  FileText,
  Save,
  Loader2,
  Plus,
  Trash2,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const PAGES = [
  {
    slug: 'privacy-policy',
    label: 'Privacy Policy',
    publicPath: '/privacy_policy',
    Icon: ShieldCheck,
  },
  {
    slug: 'data-disclaimer',
    label: 'Data Disclaimer',
    publicPath: '/disclaimer',
    Icon: AlertCircle,
  },
  {
    slug: 'terms-of-service',
    label: 'Terms of Service',
    publicPath: '/terms',
    Icon: FileText,
  },
];

const emptyContent = () => ({
  title: '',
  subtitle: '',
  last_updated: '',
  intro: '',
  sections: [],
  footer_note: '',
  seo: { title: '', description: '' },
});

const LegalPagesManager = () => {
  const { token } = useCMSAuth();
  const [activeSlug, setActiveSlug] = useState(PAGES[0].slug);
  const [contentBySlug, setContentBySlug] = useState({});
  const [initialBySlug, setInitialBySlug] = useState({});
  const [isActiveBySlug, setIsActiveBySlug] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          PAGES.map((p) =>
            fetch(`${API_URL}/api/cms/pages/${p.slug}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }).then((r) => (r.ok ? r.json() : { page_slug: p.slug, content: emptyContent(), is_active: true })),
          ),
        );
        const nextContent = {};
        const nextActive = {};
        results.forEach((r, idx) => {
          const slug = PAGES[idx].slug;
          const merged = { ...emptyContent(), ...(r.content || {}) };
          if (!Array.isArray(merged.sections)) merged.sections = [];
          if (!merged.seo || typeof merged.seo !== 'object') merged.seo = { title: '', description: '' };
          nextContent[slug] = merged;
          nextActive[slug] = r.is_active !== false;
        });
        setContentBySlug(nextContent);
        setInitialBySlug(JSON.parse(JSON.stringify(nextContent)));
        setIsActiveBySlug(nextActive);
      } catch (e) {
        toast.error('Could not load legal pages content.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const current = contentBySlug[activeSlug] || emptyContent();
  const isActive = isActiveBySlug[activeSlug] !== false;
  const hasChanges =
    JSON.stringify(current) !== JSON.stringify(initialBySlug[activeSlug] || emptyContent());

  const update = (patch) => {
    setContentBySlug((prev) => ({ ...prev, [activeSlug]: { ...prev[activeSlug], ...patch } }));
  };

  const updateSection = (idx, patch) => {
    setContentBySlug((prev) => {
      const secs = [...(prev[activeSlug]?.sections || [])];
      secs[idx] = { ...secs[idx], ...patch };
      return { ...prev, [activeSlug]: { ...prev[activeSlug], sections: secs } };
    });
  };

  const addSection = () => {
    setContentBySlug((prev) => {
      const secs = [...(prev[activeSlug]?.sections || [])];
      const nextId = `sec-${Date.now()}`;
      secs.push({ id: nextId, title: 'New Section', body_html: '<p>Write content here…</p>' });
      return { ...prev, [activeSlug]: { ...prev[activeSlug], sections: secs } };
    });
  };

  const removeSection = (idx) => {
    if (!window.confirm('Delete this section?')) return;
    setContentBySlug((prev) => {
      const secs = [...(prev[activeSlug]?.sections || [])];
      secs.splice(idx, 1);
      return { ...prev, [activeSlug]: { ...prev[activeSlug], sections: secs } };
    });
  };

  const moveSection = (idx, delta) => {
    setContentBySlug((prev) => {
      const secs = [...(prev[activeSlug]?.sections || [])];
      const j = idx + delta;
      if (j < 0 || j >= secs.length) return prev;
      [secs[idx], secs[j]] = [secs[j], secs[idx]];
      return { ...prev, [activeSlug]: { ...prev[activeSlug], sections: secs } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${activeSlug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content: current, is_active: isActive }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setInitialBySlug((prev) => ({ ...prev, [activeSlug]: JSON.parse(JSON.stringify(current)) }));
      toast.success('Saved. Changes are live on the public site.');
    } catch (e) {
      toast.error('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (nextActive) => {
    setIsActiveBySlug((prev) => ({ ...prev, [activeSlug]: nextActive }));
    // Persist immediately by calling save (no full state re-fetch)
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${activeSlug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content: current, is_active: nextActive }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(nextActive ? 'Page published.' : 'Page set to draft (hidden from public).');
    } catch (e) {
      toast.error('Could not update publish status.');
      setIsActiveBySlug((prev) => ({ ...prev, [activeSlug]: !nextActive }));
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  const activePage = PAGES.find((p) => p.slug === activeSlug);

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="legal-pages-manager">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-ux-cyan" /> Legal Pages
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage the Privacy Policy, Data Disclaimer and Terms of Service pages. Each section
              accepts HTML (safe tags only) so you can add links and lists.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild data-testid="legal-preview-btn">
              <a href={activePage?.publicPath} target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Preview {activePage?.label}
              </a>
            </Button>
            <Button onClick={save} disabled={!hasChanges || saving} data-testid="legal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save Changes
            </Button>
          </div>
        </div>

        <Tabs value={activeSlug} onValueChange={setActiveSlug}>
          <TabsList className="grid grid-cols-3 max-w-2xl">
            {PAGES.map((p) => (
              <TabsTrigger key={p.slug} value={p.slug} data-testid={`legal-tab-${p.slug}`}>
                <p.Icon className="w-4 h-4 mr-1.5" /> {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {PAGES.map((p) => (
            <TabsContent key={p.slug} value={p.slug} className="space-y-6 mt-6">
              <PublishStatusCard
                isPublished={isActive}
                onChange={toggleActive}
                entityLabel="page"
                publicRoute={p.publicPath}
                testId={`legal-publish-${p.slug}`}
              />

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Page Header</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={current.title || ''}
                        onChange={(e) => update({ title: e.target.value })}
                        data-testid="legal-title-input"
                      />
                    </div>
                    <div>
                      <Label>Last Updated (display text)</Label>
                      <Input
                        placeholder="e.g. February 12, 2026"
                        value={current.last_updated || ''}
                        onChange={(e) => update({ last_updated: e.target.value })}
                        data-testid="legal-last-updated-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Subtitle (short line under the title)</Label>
                    <Input
                      value={current.subtitle || ''}
                      onChange={(e) => update({ subtitle: e.target.value })}
                      data-testid="legal-subtitle-input"
                    />
                  </div>
                  <div>
                    <Label>Introduction Paragraph</Label>
                    <Textarea
                      rows={4}
                      value={current.intro || ''}
                      onChange={(e) => update({ intro: e.target.value })}
                      data-testid="legal-intro-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Sections ({(current.sections || []).length})</CardTitle>
                  <Button size="sm" variant="outline" onClick={addSection} data-testid="legal-add-section-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Section
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(current.sections || []).length === 0 && (
                    <p className="text-sm text-slate-500">
                      No sections yet. Click "Add Section" to get started.
                    </p>
                  )}
                  {(current.sections || []).map((sec, idx) => (
                    <div
                      key={sec.id || idx}
                      className="border rounded-lg p-4 space-y-3 bg-slate-50/50"
                      data-testid={`legal-section-editor-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          className="flex-1"
                          value={sec.title || ''}
                          placeholder="Section title"
                          onChange={(e) => updateSection(idx, { title: e.target.value })}
                          data-testid={`legal-section-title-${idx}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => moveSection(idx, -1)}
                          disabled={idx === 0}
                          data-testid={`legal-section-up-${idx}`}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => moveSection(idx, 1)}
                          disabled={idx === (current.sections || []).length - 1}
                          data-testid={`legal-section-down-${idx}`}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeSection(idx)}
                          data-testid={`legal-section-delete-${idx}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">
                          Body (HTML allowed — use &lt;p&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;a href=&quot;…&quot;&gt;, &lt;b&gt;, &lt;i&gt;)
                        </Label>
                        <Textarea
                          rows={6}
                          className="font-mono text-sm"
                          value={sec.body_html || ''}
                          onChange={(e) => updateSection(idx, { body_html: e.target.value })}
                          data-testid={`legal-section-body-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Footer Note & SEO</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Footer Note (small box shown at the bottom)</Label>
                    <Textarea
                      rows={3}
                      value={current.footer_note || ''}
                      onChange={(e) => update({ footer_note: e.target.value })}
                      data-testid="legal-footer-note-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>SEO Title</Label>
                      <Input
                        value={current.seo?.title || ''}
                        onChange={(e) => update({ seo: { ...(current.seo || {}), title: e.target.value } })}
                        data-testid="legal-seo-title-input"
                      />
                    </div>
                    <div>
                      <Label>SEO Description</Label>
                      <Input
                        value={current.seo?.description || ''}
                        onChange={(e) =>
                          update({ seo: { ...(current.seo || {}), description: e.target.value } })
                        }
                        data-testid="legal-seo-description-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default LegalPagesManager;
